import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "./primitives";
import { API, LOGO_MARK } from "../lib/site";

export const Newsletter = () => {
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        const er = {};
        if (firstName.trim().length < 2) er.first_name = "Please enter your first name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) er.email = "Please enter a valid email address.";
        setErrors(er);
        if (Object.keys(er).length) return;
        setStatus("submitting");
        try {
            await axios.post(`${API}/newsletter`, { first_name: firstName, email });
            setStatus("success");
        } catch {
            setStatus("idle");
            toast.error("Something went wrong. Please try again.");
        }
    };

    return (
        <section data-testid="newsletter-section" className="grain relative overflow-hidden bg-[#04150D] py-20 sm:py-24">
            <img
                src={LOGO_MARK}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-24 top-1/2 w-[400px] -translate-y-1/2 opacity-[0.06]"
            />
            <div className="relative z-[2] mx-auto grid max-w-[1440px] items-center gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:px-12">
                <Reveal>
                    <p className="eyebrow-gold">Newsletter</p>
                    <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                        Stay Connected With GGH
                    </h2>
                    <p className="mt-5 max-w-lg text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                        Receive occasional updates on sustainable hospitality, food-waste
                        innovation, research and GGH initiatives.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    {status === "success" ? (
                        <div
                            data-testid="newsletter-success"
                            role="status"
                            className="flex items-start gap-4 rounded-2xl border border-[#C2A160]/40 bg-[#07211A] p-7"
                        >
                            <CheckCircle2 size={26} className="mt-0.5 shrink-0 text-[#D9B96A]" aria-hidden="true" />
                            <p className="text-sm leading-relaxed text-[#FDFBF7]/85">
                                Thank you for subscribing. You will receive occasional updates from
                                Global Green Hotel — no noise, just meaningful progress.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} noValidate data-testid="newsletter-form" className="rounded-2xl border border-white/10 bg-[#07211A] p-7">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="nl-name" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                        First Name
                                    </label>
                                    <input
                                        id="nl-name"
                                        data-testid="newsletter-first-name"
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => {
                                            setFirstName(e.target.value);
                                            setErrors((er) => ({ ...er, first_name: undefined }));
                                        }}
                                        aria-invalid={!!errors.first_name}
                                        className={`input-field ${errors.first_name ? "input-error" : ""}`}
                                        placeholder="First name"
                                    />
                                    {errors.first_name && <p className="mt-2 text-xs font-semibold text-[#E8A0A0]" role="alert">{errors.first_name}</p>}
                                </div>
                                <div>
                                    <label htmlFor="nl-email" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                        Email
                                    </label>
                                    <input
                                        id="nl-email"
                                        data-testid="newsletter-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setErrors((er) => ({ ...er, email: undefined }));
                                        }}
                                        aria-invalid={!!errors.email}
                                        className={`input-field ${errors.email ? "input-error" : ""}`}
                                        placeholder="you@example.com"
                                    />
                                    {errors.email && <p className="mt-2 text-xs font-semibold text-[#E8A0A0]" role="alert">{errors.email}</p>}
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="newsletter-submit"
                                disabled={status === "submitting"}
                                className="btn-gold mt-5 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {status === "submitting" ? "Subscribing…" : "Subscribe"}
                            </button>
                        </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
};
